import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as InventorySelectors from 'selectors/InventorySelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';

import ContainerBox from 'components/ContainerBox';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getSkuAllocationsByLocation, getSkuInStockAmountByLocation } from 'views/Inventory/utils';
import { ProductVariant, SkuItem } from 'common/modules/inventory/types';
import { getVariantSkuId } from 'common/modules/products/variants';
import { ProductApi } from 'common/types';
import { clearSkuItemsListener, setSkuItemsListener } from 'actions/InventoryActions';

import { Table, TableCell, TableRow, TableBody, TableHead, TableContainer } from '@mui/material';
import { Link } from 'react-router-dom';
import ColorCodedTypography from 'components/ColorCodedTypography';

interface ProductAvailability {
	product: ProductApi;
	inStock: number;
	forRent: number;
	forSale: number;
	skus: SkuItem[];
}

const PackageAvailability = () => {
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const productsById = useSelector(StockSelectors.stockProductsById);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const skuItems = useSelector(InventorySelectors.skuItems);
	const dispatch = useDispatch();
	const { t } = useTranslation();

	useEffect(() => {
		dispatch(setSkuItemsListener(shopId));
		return () => {
			dispatch(clearSkuItemsListener());
		};
	}, [dispatch, shopId]);

	const setProducts = useMemo(() => {
		if (product.setProductIds)
			return product.setProductIds.map((id) => productsById[id]).filter((product) => !!product);
		else return [];
	}, [product, productsById]);

	const getAvailabilities = (product: ProductApi) => {
		const availabilities: ProductAvailability = {
			product: product,
			inStock: 0,
			forRent: 0,
			forSale: 0,
			skus: [],
		};
		const variants: ProductVariant[] = product.variants.options;

		for (let variant of variants) {
			const selectedSkuId = getVariantSkuId(variant);
			if (skuItems.data === null) break;
			const selectedSku = skuItems.data.find((sku) => sku.id === selectedSkuId);
			if (!selectedSku) {
				availabilities.inStock += Infinity;
				availabilities.forRent += Infinity;
				availabilities.forSale += Infinity;
				break;
			}
			if (availabilities.skus.find((sku) => sku.id === selectedSkuId)) {
				break;
			}
			const inStock = getSkuInStockAmountByLocation(selectedSku, activeLocationId);
			const forRent = getSkuAllocationsByLocation(selectedSku, activeLocationId, 'rental');
			const forSale = getSkuAllocationsByLocation(selectedSku, activeLocationId, 'sales');
			availabilities.inStock += inStock;
			availabilities.forRent += forRent;
			availabilities.forSale += forSale;
			availabilities.skus.push(selectedSku);
		}
		return availabilities;
	};

	const rows = setProducts.map((product) => getAvailabilities(product));

	const setAvailability = {
		inStock: Math.min(...rows.map((row) => row.inStock)),
		forRent: Math.min(...rows.map((row) => row.forRent)),
		forSale: Math.min(...rows.map((row) => row.forSale)),
	};

	const diplayAmount = (value: number) => {
		switch (value) {
			case Infinity:
				return '∞';
			default:
				return value;
		}
	};

	return (
		<TableContainer
			component={({ children }) => <ContainerBox padding="none">{children}</ContainerBox>}
		>
			<Box sx={{ padding: 3 }}>
				<Typography variant="h6">{t('product.inventory', 'Inventory')}</Typography>
				<Typography mb={2} variant="body2" color="secondary">
					{t(
						'product.packageInventory',
						'Product package uses inventory of the products included in it. The product package is available when all included products are available.',
					)}
				</Typography>
			</Box>
			<Table sx={{ minWidth: 700 }} aria-label="spanning table">
				<TableHead>
					<TableRow>
						<TableCell colSpan={3}>{t('product.name', 'Name')}</TableCell>
						<TableCell colSpan={3}>{t('product.SKU', 'SKU')}</TableCell>
						<TableCell>{t('product.inStock', 'In stock')}</TableCell>
						<TableCell>{t('product.forRent', 'For rent')}</TableCell>
						<TableCell>{t('product.forSale', 'For sale')}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow sx={{ height: '8rem' }}>
						<TableCell sx={{ fontWeight: 'bold' }} colSpan={3}>
							{product.name.def}
						</TableCell>
						<TableCell colSpan={3}>-</TableCell>
						<TableCell>
							<ColorCodedTypography value={setAvailability.inStock}>
								{diplayAmount(setAvailability.inStock)}
							</ColorCodedTypography>
						</TableCell>
						<TableCell>
							<ColorCodedTypography value={setAvailability.forRent}>
								{diplayAmount(setAvailability.forRent)}
							</ColorCodedTypography>
						</TableCell>
						<TableCell>
							<ColorCodedTypography value={setAvailability.forSale}>
								{diplayAmount(setAvailability.forSale)}
							</ColorCodedTypography>
						</TableCell>
					</TableRow>
					{rows.map((availability) => (
						<TableRow key={availability.product.id}>
							<TableCell colSpan={1} />
							<TableCell colSpan={2}>
								<Link to={`/catalog/products/${availability.product.id}/availability`}>
									{availability.product.name.def}
								</Link>
							</TableCell>
							<TableCell colSpan={3}>
								{availability.skus.map((sku) => sku.skuCode).join(', ')}
							</TableCell>
							<TableCell>{diplayAmount(availability.inStock)}</TableCell>
							<TableCell>{diplayAmount(availability.forRent)}</TableCell>
							<TableCell>{diplayAmount(availability.forSale)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default PackageAvailability;
